import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Stack,
  Text,
  useToast,
  VStack,
  HStack,
  Image,
  Badge,
} from '@chakra-ui/react';
import { FaCoins, FaExchangeAlt, FaInfoCircle, FaShoppingCart, FaHistory } from 'react-icons/fa';
import { COLORS } from '../utils/constants';
import XamanWalletConnect from '../components/XamanWalletConnect';
import useWallet, { WALLET_STATUS } from '../store/useWallet';

const BuyXGC = () => {
  const toast = useToast();
  const { status, address, xrpBalance, sendXgc } = useWallet();
  
  const [amount, setAmount] = useState(100);
  const [paymentMethod, setPaymentMethod] = useState('xrp');
  const [recipient, setRecipient] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Mock exchange rates
  const exchangeRates = {
    xrp: 0.5, // 1 XRP = 0.5 XGC
    usd: 0.1, // 1 USD = 0.1 XGC
    eur: 0.095, // 1 EUR = 0.095 XGC
  };
  
  // Calculate token amount based on selected payment method and exchange rate
  const calculateTokens = () => {
    return amount * (1 / exchangeRates[paymentMethod]);
  };
  
  // Get formatted price with currency symbol
  const getFormattedPrice = () => {
    const currencySymbols = {
      xrp: 'XRP',
      usd: '$',
      eur: '€',
    };
    
    return `${currencySymbols[paymentMethod]}${amount}`;
  };
  
  // Handle buy action
  const handleBuy = () => {
    if (status !== WALLET_STATUS.CONNECTED) {
      toast({
        title: 'Wallet not connected',
        description: 'Please connect your wallet to continue',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (paymentMethod === 'xrp' && amount > xrpBalance) {
      toast({
        title: 'Insufficient balance',
        description: `You need at least ${amount} XRP in your wallet`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsProcessing(true);
    
    // Simulate API call to purchase tokens
    setTimeout(() => {
      toast({
        title: 'Purchase successful',
        description: `You've purchased ${calculateTokens().toFixed(2)} XGC`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsProcessing(false);
    }, 2000);
  };
  
  // Handle send XGC action
  const handleSendXGC = async () => {
    if (!recipient) {
      toast({
        title: 'Missing recipient',
        description: 'Please enter a valid recipient address',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const result = await sendXgc(recipient, amount);
      
      if (result.success) {
        toast({
          title: 'Transaction submitted',
          description: `Transaction ID: ${result.txId}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error?.message || 'Transaction failed');
      }
    } catch (error) {
      toast({
        title: 'Transaction failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <Container maxWidth="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" mb={2}>
            Buy & Manage XGC Tokens
          </Heading>
          <Text color={COLORS.textSecondary}>
            Purchase XGC tokens to use for all services on our platform
          </Text>
        </Box>
        
        {/* Wallet Connection */}
        <Box
          p={6}
          bg="rgba(0, 0, 0, 0.5)"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="rgba(30, 220, 11, 0.3)"
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            gap={4}
          >
            <VStack align="flex-start" spacing={1}>
              <Heading as="h2" size="md">
                Connect Your Wallet
              </Heading>
              <Text fontSize="sm" color={COLORS.textSecondary}>
                Connect your Xaman wallet to buy and manage XGC tokens
              </Text>
            </VStack>
            
            <XamanWalletConnect />
          </Flex>
        </Box>
        
        {/* Token Purchase Form */}
        <Box
          p={6}
          bg="rgba(0, 0, 0, 0.5)"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="rgba(30, 220, 11, 0.3)"
        >
          <Heading as="h2" size="md" mb={4}>
            <FaShoppingCart /> Purchase XGC Tokens
          </Heading>
          
          <Stack spacing={6} direction={{ base: 'column', md: 'row' }}>
            <Box flex="1">
              <FormControl mb={4}>
                <FormLabel>Payment Method</FormLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor="rgba(30, 220, 11, 0.3)"
                  _hover={{ borderColor: COLORS.primary }}
                >
                  <option value="xrp">XRP (from wallet)</option>
                  <option value="usd">Credit/Debit Card (USD)</option>
                  <option value="eur">Credit/Debit Card (EUR)</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Amount</FormLabel>
                <NumberInput
                  min={10}
                  max={10000}
                  value={amount}
                  onChange={(value) => setAmount(parseFloat(value))}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor="rgba(30, 220, 11, 0.3)"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Box>
            
            <Box
              flex="1"
              p={4}
              border="1px dashed"
              borderColor={COLORS.primary}
              borderRadius="md"
              bg="rgba(0, 0, 0, 0.2)"
            >
              <VStack spacing={3} align="stretch">
                <Heading as="h3" size="sm" display="flex" alignItems="center">
                  <FaInfoCircle color={COLORS.primary} style={{ marginRight: '8px' }} />
                  Order Summary
                </Heading>
                
                <Divider />
                
                <Flex justify="space-between">
                  <Text>Price:</Text>
                  <Text fontWeight="bold">{getFormattedPrice()}</Text>
                </Flex>
                
                <Flex justify="space-between">
                  <Text>Exchange Rate:</Text>
                  <Text>
                    1 {paymentMethod.toUpperCase()} = {(1 / exchangeRates[paymentMethod]).toFixed(2)} XGC
                  </Text>
                </Flex>
                
                <Divider />
                
                <Flex justify="space-between" fontWeight="bold">
                  <Text>You'll receive:</Text>
                  <Text color={COLORS.primary}>
                    {calculateTokens().toFixed(2)} XGC
                  </Text>
                </Flex>
                
                <Button
                  leftIcon={<FaCoins />}
                  colorScheme="green"
                  bg={COLORS.primary}
                  color="black"
                  size="lg"
                  width="100%"
                  mt={2}
                  onClick={handleBuy}
                  isLoading={isProcessing}
                  loadingText="Processing"
                  isDisabled={status !== WALLET_STATUS.CONNECTED && paymentMethod === 'xrp'}
                >
                  Buy XGC Tokens
                </Button>
                
                <Text fontSize="xs" color={COLORS.textSecondary} textAlign="center">
                  By proceeding, you agree to our <Link color={COLORS.primary}>Terms of Service</Link>
                </Text>
              </VStack>
            </Box>
          </Stack>
        </Box>
        
        {/* Send XGC Section */}
        <Box
          p={6}
          bg="rgba(0, 0, 0, 0.5)"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="rgba(30, 220, 11, 0.3)"
        >
          <Heading as="h2" size="md" mb={4}>
            <FaExchangeAlt /> Send XGC Tokens
          </Heading>
          
          <Stack spacing={6} direction={{ base: 'column', md: 'row' }}>
            <Box flex="1">
              <FormControl mb={4}>
                <FormLabel>Recipient Address</FormLabel>
                <Input
                  placeholder="Enter recipient XRPL address"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor="rgba(30, 220, 11, 0.3)"
                  _hover={{ borderColor: COLORS.primary }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Amount to Send</FormLabel>
                <NumberInput
                  min={1}
                  max={10000}
                  value={amount}
                  onChange={(value) => setAmount(parseFloat(value))}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderColor="rgba(30, 220, 11, 0.3)"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Box>
            
            <Box flex="1">
              <VStack spacing={4} align="stretch">
                <Box
                  p={4}
                  bg="rgba(0, 0, 0, 0.2)"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="rgba(30, 220, 11, 0.2)"
                >
                  <Text fontWeight="bold" mb={2}>Transaction Details</Text>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm">Asset:</Text>
                    <Badge colorScheme="green">XGC Token</Badge>
                  </HStack>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm">Network Fee:</Text>
                    <Text fontSize="sm">~0.000012 XRP</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="bold">Total:</Text>
                    <Text fontSize="sm" fontWeight="bold">{amount} XGC</Text>
                  </HStack>
                </Box>
                
                <Button
                  colorScheme="green"
                  bg={COLORS.primary}
                  color="black"
                  size="lg"
                  width="100%"
                  onClick={handleSendXGC}
                  isLoading={isProcessing}
                  loadingText="Sending"
                  isDisabled={status !== WALLET_STATUS.CONNECTED || !recipient}
                >
                  Send XGC
                </Button>
                
                <Text fontSize="xs" color={COLORS.textSecondary} textAlign="center">
                  All transactions are irreversible. Please double-check the recipient address.
                </Text>
              </VStack>
            </Box>
          </Stack>
        </Box>
        
        {/* Transaction History */}
        <Box
          p={6}
          bg="rgba(0, 0, 0, 0.5)"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="rgba(30, 220, 11, 0.3)"
        >
          <Heading as="h2" size="md" mb={4} display="flex" alignItems="center">
            <FaHistory style={{ marginRight: '8px' }} /> Transaction History
          </Heading>
          
          {status === WALLET_STATUS.CONNECTED ? (
            <Box
              p={4}
              bg="rgba(0, 0, 0, 0.2)"
              borderRadius="md"
              borderWidth="1px"
              borderColor="rgba(30, 220, 11, 0.2)"
              textAlign="center"
            >
              <Text mb={4}>Your recent XGC transactions will appear here</Text>
              
              <Button
                variant="outline"
                borderColor={COLORS.primary}
                color={COLORS.primary}
                size="sm"
              >
                View All Transactions
              </Button>
            </Box>
          ) : (
            <Box
              p={4}
              bg="rgba(0, 0, 0, 0.2)"
              borderRadius="md"
              textAlign="center"
            >
              <Text color={COLORS.textSecondary} mb={2}>
                Connect your wallet to view transaction history
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
    </Container>
  );
};

export default BuyXGC; 